<template>
  <div
    class="participant-exercises-container"
    :class="columnLayoutActive ? 'column-layout' : ''"
  >
    <v-container>
      <drag-selector
        :disabled="disabledDragSelector"
        v-model="checkedList"
        ref="dragSelector"
        :cancel-over-view="true"
        @change="handleDragSelectorChange"
      >
        <div
          :class="`day ${copiedExerciseDay === day.date ? 'bhBlue4' : ''}`"
          v-for="day in daysMerged"
          :key="day.id"
        >
          <div class="day-index">
            <div class="day-index--wrapper">
              <div class="day-index--day">
                {{ $moment(day.date).format('dd') }}
                <span>{{ $moment(day.date).format('DD') }}</span>
              </div>
            </div>
          </div>
          <div class="day-column additional" v-if="!pasting">
            <div class="new-exercise" @click="$emit('new-exercise', day.date)">
              <v-icon size="16">bhi-plus</v-icon>
            </div>
            <div
              class="remove-all-exercises"
              v-if="day.items.length > 0"
              @click="$emit('remove-all-exercises', day.date)"
            >
              <v-icon size="16">bhi-trash</v-icon>
            </div>
            <div
              class="copy-exercise-day"
              v-if="day.items.length > 0"
              @click="$emit('copy-exercise-day', day.date)"
            >
              <v-icon size="16">bhi-copy-file</v-icon>
            </div>
          </div>
          <div class="day-column">
            <draggable
              group="exercises"
              @end="dragEnd($event, day.id)"
              @start="dragStart"
              :move="checkMove"
              @change="changed(day.date, $event)"
              :options="{ filter: '.v-text-field', preventOnFilter: false }"
              :list="day.items"
              :key="'column-' + day.id"
              class="day-column-wrap"
              :disabled="disabledDragExercise"
            >
              <drag-selector-item
                componentName="exercise-tile"
                ref="dragSelectorItem"
                v-for="(exercise, id) in day.items"
                :allow-hide-details="true"
                :default-hide-details="true"
                :remember-selection="true"
                :is-selected="
                  hasSelectedItems &&
                  checkedList.find(el => el.id === exercise.id)
                    ? true
                    : false
                "
                :key="`column-${day.id}-${id}-${exercise.id}`"
                :id="exercise.id"
                :calendar-id="exercise.calendarId"
                :title="exercise.title"
                :description="exercise.description"
                :media="exercise.media"
                :additional-media="exercise.additionalMedia"
                :mobile-static-media="exercise.mobileStaticMedia"
                :editable="true"
                :quick-editable="
                  !!(
                    !hasSelectedItems ||
                    (checkedList.length === 1 &&
                      checkedList.find(el => el.id === exercise.id))
                  )
                "
                :hide-menu="
                  !!(
                    hasSelectedItems ||
                    (checkedList.length === 1 &&
                      !checkedList.find(el => el.id === exercise.id))
                  )
                "
                :type="exercise.type"
                :ownership="
                  exercise.type == 'ACTIVITY' ? exercise.activity.type : false
                "
                :disabled-click="pasting"
                :multi-thumbnail="exercise.multiThumbnail"
                :exercise="
                  exercise.type == 'EXERCISE' ? exercise.exercise : false
                "
                :series="exercise.type == 'SERIES' ? exercise.series : false"
                :activity="
                  exercise.type == 'ACTIVITY' ? exercise.activity : false
                "
                :hoverLoader="progressBarsValues[exercise.id]"
                :time="
                  exercise.type == 'ACTIVITY' ? exercise.activity.time : false
                "
                :duplicable="true"
                :copyable="true"
                :copied="checkCopy(exercise.id, exercise.type)"
                :dragSelectorElement="{ ...exercise, day: day.id }"
                @mousedown.native.stop="_ => null"
                @mouseover="
                  exerciseOpenHover(
                    exercise.id,
                    exercise.media,
                    exercise.mobileStaticMedia,
                    exercise.additionalMedia,
                  )
                "
                @mouseleave="exerciseClearHover(exercise.id)"
                @stopHoverPopup="
                  exerciseClearHover(exercise.id)
                  exerciseCloseHover()
                "
                @delete="
                  removeExercise(exercise.id, exercise.type, exercise.inSeries)
                "
                @edit="editExercise(exercise, day.date)"
                @quick-edit="$emit('quick-edit', exercise, $event)"
                @series-quick-edit="$emit('series-quick-edit', $event)"
                @duplicate="
                  $emit('duplicate-exercise', exercise.id, exercise.type)
                "
                @copy="
                  $emit(
                    'copy-exercise',
                    exercise.id,
                    exercise.type,
                    exercise.calendarId,
                  )
                "
              />
              <div
                v-if="pasting"
                :class="
                  `paste-tile ${
                    selectedPaste.includes(day.date)
                      ? 'bhBlue1 bhWhite--text'
                      : ''
                  }`
                "
                slot="footer"
                @click="selectPaste(day.date)"
              >
                paste
              </div>
            </draggable>
          </div>
        </div>
      </drag-selector>
    </v-container>
    <v-dialog
      v-model="exerciseHover"
      hide-overlay
      attach=".participant-exercises-container"
      content-class="exercises-hover-dialog"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card @mouseleave.native.stop="exerciseCloseHover()">
        <img
          v-if="exerciseHoverUrl && exerciseHoverType == 'base64'"
          :src="'data:image/jpeg;base64,' + exerciseHoverUrl"
        />
        <img
          v-else-if="exerciseHoverUrl && exerciseHoverType == 'url'"
          :src="exerciseHoverUrl"
        />
        <v-btn color="white" icon size="24" @click="exerciseCloseHover">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import DragSelector from '@/components/dragSelector/DragSelector.vue'
import DragSelectorItem from '@/components/dragSelector/DragSelectorItem.vue'

export default {
  name: 'ParticipantExercisesContainer',
  data() {
    return {
      columns: [],
      daysMerged: false,
      draggedExerciseId: null,
      draggedExerciseList: [],
      draggedExerciseDay: null,
      draggedExerciseChange: false,
      exerciseDay: null,
      exerciseHover: false,
      exerciseHoverUrl: null,
      exerciseHoverType: 'base64',
      exerciseHoverTimeout: null,
      exerciseHoverInterval: null,
      progressBarsValues: {},
      checkedList: [],
    }
  },
  components: {
    draggable,
    DragSelector,
    DragSelectorItem,
  },
  props: {
    days: {
      type: Array,
      default: () => {
        return []
      },
    },
    isProcedureDateRelated: {
      type: Boolean,
      default: false,
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    columnLayoutActive: {
      type: Boolean,
      default: true,
    },
    selectedPaste: {
      type: Array,
      default: () => [],
    },
    pasting: {
      type: Boolean,
      default: false,
    },
    copiedExercise: {
      type: [Object, null],
      default: null,
    },
    copiedExercises: {
      type: Array,
      default: () => [],
    },
    copiedExerciseDay: {
      type: [String, null],
      default: null,
    },
    selectedExercises: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {},
  watch: {
    days(val) {
      if (val) this.daysMerged = this.mergeDays()
    },
    startDate(val) {
      if (val) this.$emit('change')
    },
    checkedList: {
      handler: function(newVal) {
        this.$emit('update:selectedExercises', [...newVal])
      },
      deep: true,
    },
  },
  computed: {
    disabledDragSelector() {
      return (
        !!this.pasting ||
        !!this.copiedExercise ||
        this.copiedExercises.length > 0
      )
    },
    disabledDragExercise() {
      return !!(this.disabledDragSelector || this.hasSelectedItems)
    },
    daysDefinitions() {
      let arr = []
      let index = 0
      for (let i = 0; i < 7; i++) {
        arr[i] = { id: i - index, date: this.weekDays[i] }
      }
      return arr
    },
    weekDays() {
      if (this.startDate == '' || this.endDate == '') return []

      let now = this.$moment(this.startDate).clone(),
        dates = []

      while (now.isSameOrBefore(this.$moment(this.endDate))) {
        dates.push(now.format('YYYY-MM-DD'))
        now.add(1, 'days')
      }
      return dates
    },
    hasSelectedItems() {
      return this.checkedList?.length ?? 0
    },
  },
  methods: {
    deselectAllExercises() {
      this.$refs.dragSelector.cancelAllSelect()
    },
    handleDragSelectorChange(list = []) {
      console.log('selectedItems', [...list])
    },
    mergeDays() {
      let arr = []
      this.setColumns(0)
      this.daysDefinitions.forEach(el => {
        let tmp = { date: el.date, id: el.id, title: '', items: [] }
        let tmp2 = this.days.find(item => item.date == el.date)
        if (tmp2 && tmp2.items) {
          tmp.items = tmp2.items
          tmp.items.map(item => {
            item.calendarId = item.id
            if (item.type == 'EXERCISE') {
              item.id = item.exercise.id
              item.title = item.exercise.title
              item.description = item.exercise.description
              item.media = item.exercise.media
              item.additionalMedia = item.exercise.additionalMedia
              item.mobileStaticMedia = item.exercise.mobileStaticMedia
              item.inSeries = item.exercise.inSeries || false
            } else if (item.type == 'SERIES') {
              item.id = item.series.id
              item.title = item.series.title
              item.media = item.series.media
              item.additionalMedia = item.series.additionalMedia
              item.mobileStaticMedia = item.series.mobileStaticMedia
              item.multiThumbnail = item.series.exercises
              item.inSeries = item.series.inSeries || false
            } else {
              item.id = item.activity.id
              item.title = item.activity.title
              item.description = item.activity.description
              item.media = item.activity.media
              item.additionalMedia = item.activity.additionalMedia
              item.mobileStaticMedia = item.activity.mobileStaticMedia
              item.inSeries = item.activity.inSeries || false
            }
            return item
          })
          tmp.title = tmp2.title
          if (tmp.items.length > this.columns.length || !this.columns)
            this.setColumns(tmp.items.length)
        }
        arr.push(tmp)
      })
      return arr
    },
    calculateColumns() {
      let max = 0
      this.daysMerged.forEach(el => {
        if (el.items.length > this.columns.length || !this.columns) {
          this.setColumns(el.items.length)
        }
        if (el.items.length > max) max = el.items.length
      })
      if (max > 0 && max < this.columns.length) this.setColumns(max)
    },
    setColumns(length) {
      if (length > 0) {
        this.columns = Array.from(Array(length).keys())
      } else {
        this.columns = false
      }
    },
    formatTime(time) {
      return this.$moment(time, 'HH:mm:ss').format('hh:mm A')
    },
    checkMove: function(evt) {
      this.exerciseHover = false
      this.draggedExerciseId = evt.draggedContext.element.calendarId
      this.draggedExerciseList = evt.relatedContext.list
    },
    dragStart() {
      this.exerciseHover = false
      this.draggedExerciseChange = false
      this.draggedExerciseDay = null
      this.$emit('drag-start')
    },
    dragEnd(ev, day) {
      let index = this.draggedExerciseList.findIndex(
        e => e.calendarId === this.draggedExerciseId,
      )
      let data = {}
      if (index > -1) {
        if (index == 0) {
          if (this.draggedExerciseList.length > 1)
            data['itemAfter'] = this.draggedExerciseList[index + 1].calendarId
        } else if (index == this.draggedExerciseList.length - 1) {
          data['itemBefore'] = this.draggedExerciseList[index - 1].calendarId
        } else {
          data['itemBefore'] = this.draggedExerciseList[index - 1].calendarId
          data['itemAfter'] = this.draggedExerciseList[index + 1].calendarId
        }
        if (this.draggedExerciseList) {
          data['title'] = this.draggedExerciseList[index]['title']
          data['description'] = this.draggedExerciseList[index]['description']
            ? this.draggedExerciseList[index]['description']
            : ''
        }
      }
      if (this.draggedExerciseDay) data['date'] = this.draggedExerciseDay
      if (this.draggedExerciseChange) {
        this.$emit('move-exercise', this.draggedExerciseId, data)
      }
      this.$emit('drag-end')
    },
    changed(id, ev) {
      this.draggedExerciseChange = true
      if (ev.added) this.draggedExerciseDay = id
    },
    removeExercise(id, type, inSeries = false) {
      this.$emit('remove-exercise', id, type, inSeries)
    },
    editExercise(data, day) {
      this.$emit('edit-exercise', {
        id:
          data.type == 'EXERCISE'
            ? data.exercise.id
            : data.type == 'ACTIVITY'
            ? data.activity.id
            : data.series.id,
        type: data.type,
        data: data,
        day: day,
      })
    },
    exerciseOpenHover(
      id,
      media = false,
      mobileStaticMedia = null,
      additionalMedia = false,
    ) {
      clearTimeout(this.exerciseHoverTimeout)
      this.exerciseCloseHover()
      if (
        id &&
        (media?.thumbnailUrl ||
          media?.previewUrl ||
          additionalMedia?.thumbnailUrl ||
          additionalMedia?.previewUrl ||
          mobileStaticMedia?.mediaUrl)
      ) {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.exerciseHoverInterval)
          clearInterval(this.exerciseHoverInterval)
        this.exerciseHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.exerciseHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.exerciseHoverTimeout) clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHoverTimeout = setTimeout(() => {
          this.exerciseHover = null
          if (
            media?.previewUrl ||
            additionalMedia?.previewUrl ||
            mobileStaticMedia?.mediaUrl
          ) {
            this.exerciseHoverUrl =
              media?.previewUrl ||
              additionalMedia?.previewUrl ||
              mobileStaticMedia?.mediaUrl
            this.exerciseHoverType = this.isStringBase64(
              media?.previewUrl ||
                additionalMedia?.previewUrl ||
                mobileStaticMedia?.mediaUrl,
            )
              ? 'base64'
              : 'url'
            this.exerciseHover = true
          }
        }, 1000)
      } else this.exerciseClearHover(id)
    },
    exerciseClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.exerciseHoverInterval)
      if (!this.exerciseHover) {
        clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHover = false
        this.exerciseHoverUrl = null
        this.exerciseHoverType = 'base64'
      }
    },
    exerciseCloseHover() {
      this.exerciseHover = false
    },
    onClickOutside() {
      this.exerciseCloseHover()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.exerciseCloseHover()
    },
    selectPaste(date) {
      this.$emit('select-paste-day', date)
    },
    paste(date) {
      this.$emit('paste-exercise', date)
    },
    checkCopy(id, type) {
      return this.copiedExercise
        ? id == this.copiedExercise.id && type == this.copiedExercise.type
        : false
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables';
.participant-exercises-container {
  background: $bh-white;
  width: 100%;
  // max-height: calc(100vh - 80px);
  flex-grow: 1;
  overflow: auto;
  > .container {
    min-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .container {
    display: table;
  }
  .day {
    display: flex;
    position: relative;
    min-height: 92px;
    padding-right: 60px;
    &:hover {
      .copy-exercise-day,
      .remove-all-exercises {
        opacity: 1 !important;
      }
      .paste-tile {
        display: flex;
      }
    }
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      width: 100%;
      border-bottom: 1px solid $bh-blue--4;
      left: 0;
      z-index: 1;
    }
    &.with-procedure {
      position: relative;
      &::after {
        background: $bh-red--light;
        content: '';
        opacity: 0.2;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 0;
      }
      * {
        z-index: 1;
      }
    }
    &-index {
      display: flex;
      align-items: center;
      white-space: nowrap;
      &.procedure-related {
        width: 120px;
      }
      &--wrapper {
        display: flex;
        align-items: center;
      }
      &--day {
        text-align: left;
        font-size: 9px;
        padding: 0 5px;
        color: $bh-gray--2;
        span {
          display: block;
          font-weight: bold;
          font-size: 12px;
        }
      }
      &--name {
        width: 150px;
        .bh-text-input--wrap {
          margin-bottom: 0;
        }
        .bh-text-input.active {
          background: $bh-blue--4;
        }
        .v-input {
          font-size: 12px;
        }
      }
      &--procedure {
        color: $bh-white;
        background: $bh-red;
        opacity: 0.8;
        margin-left: 10px;
        margin-right: 10px;
        border-radius: 22px;
        width: 72px;
        text-align: center;
        line-height: 26px;
        font-size: 9px;
        text-transform: uppercase;
        font-weight: bold;
      }
    }
    &-column {
      display: flex;
      flex-grow: 1;
      flex-direction: column;

      &-wrap {
        display: flex;
        flex-wrap: wrap;

        & > .drag-selector-item-wrapper {
          max-width: 100%;
        }
      }
      &.additional {
        padding-top: 0px;
        flex-grow: 0;
        justify-content: center;
        .new-exercise {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          padding: $gutter;
          border-radius: 8px;
          transition-duration: 150ms;
          background: $bh-blue--4;
          color: $bh-gray--3;
          cursor: pointer;
          font-weight: bold;
          padding: 0;
          line-height: 16px;
          .v-icon {
            margin: 10px;
            color: $bh-gray--3;
          }
          &:hover {
            background: $bh-white;
            color: $bh-blue;
            .v-icon {
              color: $bh-blue;
            }
          }
        }
        .copy-exercise-day,
        .remove-all-exercises {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: $gutter;
          border-radius: 8px;
          transition-duration: 150ms;
          background: $bh-white;
          margin-top: 8px;
          color: $bh-gray--3;
          cursor: pointer;
          font-weight: bold;
          padding: 0;
          line-height: 16px;
          opacity: 0;
          .v-icon {
            margin: 10px;
            color: $bh-red;
          }
          &:hover {
            background: $bh-red;
            color: $bh-white;
            .v-icon {
              color: $bh-white;
            }
          }
        }
        .copy-exercise-day {
          .v-icon {
            color: $bh-blue;
          }
          &:hover {
            background: $bh-blue;
            color: $bh-white;

            .v-icon {
              color: $bh-white;
            }
          }
        }
      }
    }
    .exercise-tile {
      display: flex;
      position: relative;
      padding-left: 68px;
      // border: 2px solid $bh-blue--4;

      &__text {
        flex-grow: inherit;
      }
    }
  }
  .paste-tile {
    display: flex;
    position: relative;
    border: 2px dotted $bh-blue--2;
    width: 280px;
    min-height: 74px;
    border-radius: 8px;
    justify-content: center;
    align-items: center;
    font-style: italic;
    color: $bh-blue--2;
    margin: 8px;
    // display: none;
    cursor: pointer;
    transition-duration: 200ms;
    &:hover {
      background: $bh-blue--4;
    }
  }
  .exercises-hover-dialog {
    width: auto;
    padding: 10px;
    background: $bh-white;
    .v-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 32px;
      height: 32px;
      background: $bh-blue;
    }
    img {
      display: block;
      max-height: 80vh;
      max-width: 1000px;
    }
  }
  &.column-layout {
    .container {
      padding-left: 0;
      padding-right: 0;
    }
    .drag-selector-wrapper {
      display: flex;
      height: 100%;
    }
    .day {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      white-space: normal;
      width: 14.28%;
      padding: 0 5px 70px 4px;
      min-width: 110px;
      &::before {
        display: none;
      }
      &-index {
        padding: 0 8px;
      }
      &-column {
        display: block;
        height: auto;
        width: 100%;
        padding-top: 8px;
        &-wrap {
          flex-direction: column;
        }
        &.additional {
          padding: 8px;
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          flex-wrap: wrap;
        }
        .copy-exercise-day,
        .new-exercise,
        .remove-all-exercises {
          width: 36px;
          margin-top: 0;
        }
        .copy-exercise-day,
        .remove-all-exercises {
          margin-left: 8px;
        }
        &-wrap {
          flex-wrap: wrap;
          height: 100%;
          align-items: flex-start;
          justify-content: flex-start;
        }
      }
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0px;
        width: 0;
        border-right: 1px solid $bh-blue--4;
      }
      .exercise-tile {
        width: 100%;
        margin: 0;
        min-height: 64px;
        height: auto;
        padding: 4px 8px;
        align-items: flex-start;
        margin-bottom: 12px;
        z-index: 100;
        &__thumbnail {
          width: 48px;
          height: 48px;
          &--wrap {
            width: 48px;
            height: 48px;
            position: relative;
            top: 0;
          }
        }
        &__text {
          padding: 0 0 0 8px;
          width: calc(100% - 78px);
          h6 {
            margin-bottom: 0;
          }
          h5 {
            position: static;
            max-width: 100%;
            font-size: 1rem;
          }
          p {
            position: static;
          }
        }
        &__more {
          position: absolute;
          right: 8px;
          top: 6px;
        }
        &__quick-edit {
          padding-left: 0;
        }
      }
    }
  }
}
</style>
